import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { useState } from "preact/hooks"
import { api_fetch } from "../api/client.tsx"
import { Loader2 } from "lucide-react"

interface SkillsFileResponse {
  skills_file: string | null
}

export function SkillsFileEditor() {
  const [content, setContent] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  useAsyncEffect(async () => {
    setIsLoading(true)
    const res = await api_fetch<SkillsFileResponse>("/organization/skills")
    if (res.ok) {
      setContent(res.value.data.skills_file ?? "")
    } else {
      toast({ title: "Error", description: "Failed to load AI knowledge base", variant: "destructive" })
    }
    setIsLoading(false)
  }, [])

  async function handleSave() {
    setIsSaving(true)
    const res = await api_fetch<SkillsFileResponse>("/organization/skills", {
      method: "PUT",
      body: { skills_file: content },
    })
    if (res.ok) {
      setIsDirty(false)
      toast({ title: "Saved", description: "AI knowledge base updated successfully" })
    } else {
      toast({ title: "Error", description: "Failed to save AI knowledge base", variant: "destructive" })
    }
    setIsSaving(false)
  }

  if (isLoading) {
    return (
      <div class="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">
        Add notes and context that AI wizards will reference when analyzing your deductions.
        This could include brand-specific rules, known exceptions, distributor relationships, or any other knowledge
        that helps the AI make better decisions.
      </p>
      <Textarea
        value={content}
        onChange={(e: any) => {
          setContent(e.target.value)
          setIsDirty(true)
        }}
        onKeyDown={async (e: any) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault()
            if (isDirty && !isSaving) {
              await handleSave()
            }
          }
        }}
        // @ts-ignore
        rows={12}
        class="font-mono text-sm"
        placeholder={"Example:\n- We have an ongoing spoilage allowance with KeHE of 1.5%\n- UNFI shortage claims under $50 are usually not worth disputing\n- Our Costco slotting fees are pre-negotiated annually"}
      />
      <div class="flex justify-end">
        <Button
          // @ts-ignore
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Save
        </Button>
      </div>
    </div>
  )
}
